import { Pipe, PipeTransform, inject } from '@angular/core';
import { CurrencySettingsDto } from '../../core/models/currency.models';
import { CurrencyService } from '../../core/services/currency.service';

/**
 * Impure pipe'lar uchun umumiy kesh.
 *
 * `pure: false` KERAK, chunki valyuta sozlamasi ilova ochilgandan KEYIN keladi:
 * pure pipe argumentlari o'zgarmagani uchun qayta hisoblanmasdi va ekranda eski
 * format qotib qolardi (shu sabab ilgari bootstrap bloklanib, oq ekran chiqardi).
 *
 * Impure pipe har CD siklida chaqiriladi. Katta jadvalda bu yuzlab chaqiruv
 * degani, shuning uchun natija shu yerda eslab qolinadi: kirish va sozlama
 * o'zgarmasa `formatNumber` qayta ishlamaydi. Kesh HAR BINDING uchun alohida
 * (Angular har binding'ga o'z pipe nusxasini yaratadi), ya'ni bitta yozuv yetadi.
 */
abstract class CachedMoneyPipe implements PipeTransform {
  protected readonly currency = inject(CurrencyService);

  private lastValue: number | null | undefined;
  private lastCode: string | null | undefined;
  private lastSettings: CurrencySettingsDto | null = null;
  private lastResult = '';
  private primed = false;

  protected abstract render(value: number | null | undefined, code?: string | null): string;

  transform(value: number | null | undefined, currencyCode?: string | null): string {
    // Sozlama obyekti almashsa (yuklandi yoki yangilandi) kesh eskiradi.
    const settings = this.currency.settings();

    if (
      this.primed &&
      value === this.lastValue &&
      currencyCode === this.lastCode &&
      settings === this.lastSettings
    ) {
      return this.lastResult;
    }

    this.lastValue = value;
    this.lastCode = currencyCode;
    this.lastSettings = settings;
    this.lastResult = this.render(value, currencyCode);
    this.primed = true;
    return this.lastResult;
  }
}

/**
 * Pulni valyuta belgisi bilan ko'rsatadi: `1,265,000 so'm`, `$100.00`.
 *
 * ```html
 * {{ order.totalAmount | money }}                 <!-- tenant valyutasi -->
 * {{ item.price | money: 'USD' }}                 <!-- aniq valyuta -->
 * {{ row.amount | money: row.currency }}          <!-- ma'lumotdan -->
 * ```
 *
 * Loyihada pulni `| number: '1.0-0'`, `toFixed()` yoki `toLocaleString()` bilan
 * ko'rsatish taqiqlanadi — faqat shu pipe (yoki `moneyValue`).
 */
@Pipe({ name: 'money', pure: false })
export class MoneyPipe extends CachedMoneyPipe implements PipeTransform {
  protected render(value: number | null | undefined, code?: string | null): string {
    return this.currency.format(value, code);
  }
}

/**
 * Pulni valyuta belgisiSIZ ko'rsatadi: `1,265,000`.
 *
 * Belgi yonida alohida chiqadigan joylar uchun (ustun sarlavhasi, input maydoni,
 * "so'm" so'zi allaqachon shablonda bo'lgan joylar).
 */
@Pipe({ name: 'moneyValue', pure: false })
export class MoneyValuePipe extends CachedMoneyPipe implements PipeTransform {
  protected render(value: number | null | undefined, code?: string | null): string {
    return this.currency.formatValue(value, code);
  }
}

/**
 * Pulni valyuta belgisi bilan, valyutaning STANDART kasr xonasida QOTIRILGAN
 * ko'rsatadi (`money`dagi "ortiqcha xona bo'lsa chiqariladi" qoidasisiz).
 *
 * Faqat JAMLANGAN SUMMA ko'rsatiladigan ekranlar uchun (Kassa, Moliya) — u yerda
 * har bir raqam bir nechta operatsiyaning yig'indisi, dona narxi emas, shuning
 * uchun ortiqcha kasr xonasi foydali aniqlik emas, balki hisob-kitob shovqini
 * (`0.123456789` kabi uzun qator) bo'lib chiqadi. Narx ko'rsatiladigan joyda
 * (mahsulot, kirim, sotuv) `money` ishlatilishi SHART — u yerda ortiqcha xona
 * foydalanuvchi kiritgan haqiqiy qiymat.
 */
@Pipe({ name: 'moneyFixed', pure: false })
export class MoneyFixedPipe extends CachedMoneyPipe implements PipeTransform {
  protected render(value: number | null | undefined, code?: string | null): string {
    return this.currency.formatFixed(value, code);
  }
}
